import React, { useState, useEffect, createContext, useRef } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { getName } from "country-list";
import { Skeleton } from "primereact/skeleton";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { TabView, TabPanel } from "primereact/tabview";
import ReactCountryFlag from "react-country-flag";

import StatusBadge from "../../Process/components/StatusBadge";
import RequestLayout from "./RequestLayout";
import { FEATURE_FLAGS, useFeatureFlag } from "~/config/ff";
import useRequestDashboard from "../actions/useRequestDashboard";
import { REQUEST_TYPES } from "../../Requests/useRequest";
import { Dropdown } from "primereact/dropdown";
import apiService from "~/services/service";
import API_ROUTES from "~/api-routes";
import { z } from "zod";
import { useSelector } from "react-redux";
import { getStatusOptions, StatusKey } from "~/utils/status";
import AuditSidebar from "../../../components/audit-sidebar/AuditSidebar";
export const RequestContext = createContext();

const RequestLayoutWrapper = ({ requestType }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { states } = useRequestDashboard();
  const actionsList = useRef(null);
  const { value: isDocumentStorageFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.DOCUMENT_STORAGE,
  );

  const [isAuditSidebarOpen, setIsAuditSidebarOpen] = useState(false);
  const [allowEscalationStatus, setAllowEscalationStatus] = useState(false);

  const { role, id: userId } = useSelector((state) => state.login);
  const statusOption = getStatusOptions([
    StatusKey.IN_PROGRESS,
    StatusKey.COMPLETED,
    StatusKey.NOT_STARTED,
  ]);
  const tabs = [
    { name: "Conversation", route: "conversation" },
    ...(isDocumentStorageFeatureEnabled &&
    requestType !== REQUEST_TYPES.ESCALATION
      ? [{ name: "Attachments", route: "attachments" }]
      : []),
  ];

  const activeIndex = tabs.findIndex((tab) =>
    location.pathname.includes(tab.route),
  );
  const [selectedIndex, setSelectedIndex] = useState(
    activeIndex !== -1 ? activeIndex : 2,
  );
  const [selectedStatus, setSelectedStatus] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSelectedIndex(activeIndex !== -1 ? activeIndex : 2);
  }, [location.pathname]);

  useEffect(() => {
    const assginee = states?.requestdata?.assignee;
    if (
      requestType === REQUEST_TYPES.ESCALATION &&
      assginee?.toString() !== userId?.toString()
    ) {
      setAllowEscalationStatus(true);
    } else {
      setAllowEscalationStatus(false);
    }
  }, [userId, states?.requestdata, requestType]);

  const headerTemplate = (options) => {
    return (
      <div
        className={`${options.route} header-template-container`}
        onClick={() => {
          options.onClick();
          const navigateLink =
            requestType === REQUEST_TYPES.AD_HOC
              ? `/requests/${id}/${options?.route}`
              : `/escalations/${id}/${options?.route}`;
          navigate(navigateLink);
        }}
      >
        <p className={options?.selected ? "tab-selected" : "header-value-name"}>
          {options?.name}
        </p>
      </div>
    );
  };

  const headerTitle = () => {
    if (requestType === REQUEST_TYPES.AD_HOC) {
      return (
        <p className="title">
          {`${states?.requestdata?.reason} | ${states?.requestdata?.serviceData?.name} (`}
          <ReactCountryFlag
            countryCode={states?.requestdata?.entityData?.country || ""}
            svg
            className=""
          />
          {` ${getName(states?.requestdata?.entityData?.country || "")})`}
        </p>
      );
    } else if (requestType === REQUEST_TYPES.ESCALATION) {
      return (
        <p className="title">
          {`Escalation ${states?.requestdata?.processData ? "| " + states?.requestdata?.processData?.name : states?.requestdata?.custom_process_name ? "| " + states?.requestdata?.custom_process_name : ""}`}
        </p>
      );
    }
  };
  const updateStatus = async (status) => {
    setLoading(true);
    try {
      await apiService(API_ROUTES.UPDATE_REQUEST_STATUS, "PUT", {
        body: { status: status?.code },
        replace: { id },
        schema: z.object({
          id: z.string().uuid(),
        }),
      });
      setLoading(false);
      setSelectedStatus(status);
    } catch (error) {
      setLoading(false);
    }
  };

  const statusOptionTemplate = (option) => {
    return (
      <div className="flex align-items-center">
        <StatusBadge status={{ primary_color: option?.primary_color }} />
        <div>{option?.name}</div>
      </div>
    );
  };

  useEffect(() => {
    if (states?.requestdata?.statusData) {
      setSelectedStatus({
        name: states?.requestdata?.statusData?.name,
        code: states?.requestdata?.statusData?.code,
        primary_color: states?.requestdata?.statusData?.primary_color,
        secondary_color: null,
      });
    }
  }, [states?.requestdata]);

  const checkIsDisabled = () => {
    const isRequester = states?.requestdata?.createdByData?.id === userId;

    if (role === "GXM_ADMIN") return false;

    if (states?.requestdata?.reason === "Implementation") {
      if (["GXM_MANAGER", "GXM_USER"].includes(role)) return false;
      if (["LSP_USER", "LSP_MANAGER"].includes(role)) return true;
    }
    if (
      (requestType === REQUEST_TYPES.AD_HOC &&
        ["CUSTOMER", "CUSTOMER_MANAGER"].includes(role)) ||
      allowEscalationStatus ||
      (["LSP_USER", "LSP_MANAGER", "GXM_MANAGER", "GXM_USER"].includes(role) &&
        isRequester)
    ) {
      return true;
    }
    return false;
  };

  const auditLabels = () => {
    return {
      create: "Request Created",
      update: "Information Updated",
      delete: "Request Removed",
    };
  };
  return (
    <RequestLayout
      requestData={states?.requestdata}
      loading={states?.requestLoading}
      requestType={requestType}
    >
      <RequestContext.Provider>
        <div className="process-layout-section">
          <div className="process-layout-container">
            <div className="header-container">
              <div className="title-container">
                <span
                  className="pi pi-arrow-left cursor-pointer"
                  onClick={() =>
                    navigate(
                      requestType === REQUEST_TYPES.AD_HOC
                        ? "/requests"
                        : "/escalations",
                    )
                  }
                ></span>
                {!states?.requestLoading ? (
                  <div>{headerTitle()}</div>
                ) : (
                  <Skeleton width="600px" height="20px" className="mr-1" />
                )}
              </div>
              <div className="actions-container">
                <div className="status-container">
                  <p className="status">Status</p>

                  {!states?.requestLoading ? (
                    <Dropdown
                      value={selectedStatus}
                      onChange={(e) => {
                        updateStatus(e.value);
                      }}
                      options={statusOption}
                      optionLabel="name"
                      placeholder="Select Status"
                      valueTemplate={statusOptionTemplate}
                      itemTemplate={statusOptionTemplate}
                      loading={loading}
                      disabled={checkIsDisabled()}
                    />
                  ) : (
                    <Skeleton width="200px" height="40px" className="mr-1" />
                  )}
                </div>

                {requestType === REQUEST_TYPES.AD_HOC &&
                  ["GXM_ADMIN"].includes(role) && (
                    <div className="action-button-container">
                      <Button
                        className="action-button"
                        label="Actions"
                        severity="success"
                        icon="pi pi-angle-down"
                        iconPos="right"
                        aria-haspopup
                        aria-controls="actionOverlay"
                        // loading={!process?.id}
                        onClick={(e) => actionsList.current.toggle(e)}
                      />
                      <OverlayPanel
                        className="action-panel"
                        id="actionOverlay"
                        appendTo="self"
                        dismissable
                        ref={actionsList}
                      >
                        <div className="action-list">
                          <div
                            className="action-list-item"
                            onClick={() => setIsAuditSidebarOpen(true)}
                          >
                            View History
                          </div>
                        </div>
                      </OverlayPanel>
                    </div>
                  )}
              </div>
            </div>
            <div className="tab-container">
              <TabView
                activeIndex={selectedIndex}
                onTabChange={(e) => {
                  setSelectedIndex(e.index);
                }}
              >
                {tabs.map((tab, index) => {
                  return (
                    <TabPanel
                      key={index}
                      headerTemplate={(options) =>
                        headerTemplate({
                          ...options,
                          name: tab.name,
                          route: tab.route,
                        })
                      }
                    ></TabPanel>
                  );
                })}
              </TabView>
              <Outlet />
            </div>
          </div>
        </div>
        {isAuditSidebarOpen && (
          <AuditSidebar
            heading=""
            endpoint={API_ROUTES.FETCH_REQUEST_AUDIT}
            id={id}
            labels={auditLabels}
            setSidebarToggle={setIsAuditSidebarOpen}
            sidebarToggle={isAuditSidebarOpen}
            endType="PROCESS"
          />
        )}
      </RequestContext.Provider>
    </RequestLayout>
  );
};

export default RequestLayoutWrapper;
